<template>
 <a-row v-if="result && (result.blocked || packages.length)" class="mb-5">
  <a-col :xs="24" class="mb-2">
   <label>Subsidie</label>
   <a-spin v-if="loading" size="small" style="margin-left: 10px"/>
  </a-col>
  <a-col v-if="result.blocked" :xs="24" class="mb-2">
   <a-alert type="error" show-icon message="Deze offerte kan zo niet worden aangemaakt">
    <template #description>
     <div v-for="(reason, i) in result.blockReasons" :key="i">{{ reason }}</div>
    </template>
   </a-alert>
  </a-col>
  <a-col v-for="pkg in packages" :key="pkg.subProductID" :xs="24" class="mb-2">
   <strong>{{ productName(pkg.subProductID) }}</strong>
   <a-row v-for="subsidy in pkg.subsidies" :key="subsidy.subProductID" :gutter="10">
    <a-col :xs="14">{{ subsidy.name }}</a-col>
    <a-col :xs="5" style="text-align: right">
     {{ subsidy.amount > 0 ? '- ' + formatMoney(subsidy.amount) : formatMoney(0) }}
    </a-col>
    <a-col :xs="5">
     <a-tooltip :title="subsidy.reasons.join(' · ') || null">
      <a-tag :color="statusColor(subsidy.status)">{{ statusLabel(subsidy.status) }}</a-tag>
     </a-tooltip>
    </a-col>
   </a-row>
  </a-col>
 </a-row>
</template>

<script>
import {computed, ref, watch} from "vue";
import {useStore} from "vuex";
import {DataService} from "../../../config/dataService/dataService";

const STATUS = {
 DEFINITIVE: {label: 'Definitief', color: 'green'},
 INDICATIVE: {label: 'Indicatief', color: 'orange'},
 NOT_ELIGIBLE: {label: 'Niet van toepassing', color: 'default'},
};

const SubsidyPreview = {
 name: 'SubsidyPreview',
 props: {
  productId: {type: [String, Number], required: true},
  // Productlijst van de flow; quantity > 0 = in het mandje
  products: {type: Array, default: () => []},
 },
 setup(props) {
  const {state} = useStore();
  const loading = ref(false);
  const result = ref(null);
  let timer = null;
  let requestId = 0;

  const cart = computed(() => (props.products || [])
   .filter(p => p.quantity > 0)
   .map(p => ({id: p.id, quantity: p.quantity})));

  const payload = computed(() => ({
   productID: props.productId,
   subProducts: cart.value,
   building: state.shop.order?.extraData?.building || null,
   roiInput: state.shop.order?.extraData?.roiInput || null,
  }));

  const fetchCalculation = async () => {
   if (!cart.value.length) {
    result.value = null;
    return;
   }
   const id = ++requestId;
   loading.value = true;
   try {
    const {data} = await DataService.post('offerflow/shop/calculation', payload.value);
    // Alleen het antwoord op de laatste wijziging tonen
    if (id === requestId) result.value = data;
   } catch (e) {
    if (id === requestId) result.value = null;
   } finally {
    if (id === requestId) loading.value = false;
   }
  };

  watch(() => JSON.stringify(payload.value), () => {
   clearTimeout(timer);
   timer = setTimeout(fetchCalculation, 500);
  }, {immediate: true});

  const packages = computed(() => (result.value?.packages || []).filter(p => p.subsidies?.length));

  const productName = id => (props.products || []).find(p => p.id === id)?.name || '';
  const formatMoney = amount => new Intl.NumberFormat('nl-NL', {style: 'currency', currency: 'EUR'}).format(amount || 0);
  const statusLabel = status => STATUS[status]?.label || status;
  const statusColor = status => STATUS[status]?.color || 'default';

  return {
   loading,
   result,
   packages,
   productName,
   formatMoney,
   statusLabel,
   statusColor,
  };
 },
};

export default SubsidyPreview;
</script>
